import * as React from 'react';
import { observer } from 'mobx-react';

import {
    IconTextButton,
    IAdvanceTableToolbarProps,
    DangerAlert,
    IBasicTableColumn,
    AdvanceTable,
    InformationDialogType,
    showInformationDialog,
    notification,
} from 'ui-lib';

import { OrganisationSelect } from '../../components/organisation-select';
import { hasContainsIgnoreCase, localeCompare } from 'common-utils/string-utils';
import { confirmDialog } from '../../utils';

import {
    useAccountStore,
    useAuthStore,
    useDeploymentGroupsStore,
    useDeploymentStore,
    useDeviceGroupsStore,
    useStores
} from '../../../../store';
import { DeploymentGroupType, IDisplayDeploymentGroup } from '../../../../dto/deployment/deployment-group-dto';
import { AccountType } from '../../../../dto/access-management/account-dto';

import { ACTION_TYPES, PAGE_TYPES } from '.';
import { ISaveState } from './save';

import deploymentGroupCss from './group.css';
import { DeploymentStatus } from "../../../../dto/deployment/deployment-dto";
import { getErrorMessage } from 'common-utils';
import { useManagedDeploymentGroups } from 'hooks/deployment-groups/use-manage-deployment-groups.hook';
import { TableUtils } from 'pages/private/components/table-utils';

export interface IManageDeploymentGroupsComponentProps {
    setNavigateState: (value: ISaveState) => void;
    setPageType: (type: PAGE_TYPES) => void;
    state: IListState;
}

export interface IListState {
    accountId?: number;
}

export const ManageDeploymentGroupsComponent = observer((props: IManageDeploymentGroupsComponentProps) => {

    const accountStore = useAccountStore();
    const deviceGroupStore = useDeviceGroupsStore();
    const deploymentGroupStore = useDeploymentGroupsStore();
    const deploymentStore = useDeploymentStore();
    const { assureApiAccount, currentUser } = useAuthStore();
    const { state } = props;

    const { displayDeploymentGroups, hasModifyPermission, hasModifyDeploymentGroupPermission, selectedAccount, selectAccountSource, setSelectedAccount } = useManagedDeploymentGroups({
        accountId: state?.accountId
    });

    React.useEffect(() => {
        reloadData(false);
    }, []);

    const reloadData = async (force?: boolean): Promise<void> => {
        await Promise.all([
            deploymentGroupStore.loadDeploymentGroups(assureApiAccount?.accountId, force),
        ]);
    };

    const deleteDeploymentGroup = async (record) => deploymentGroupStore.deleteDeploymentGroup(
      accountStore.getAccountByUuid(record.accountUuid)?.id,
      record.id,
      (modalType: InformationDialogType, message: string) => showInformationDialog({
          modalType,
          title: 'Deployment group',
          content: message
      })
    );

    const hasScheduledOrInProgressDeployments = (record) => {
        const groupDeployments = deploymentStore.getDeploymentsByGroupId(record.id);
        return groupDeployments.filter((deployment) => [DeploymentStatus.SCHEDULED, DeploymentStatus.IN_PROGRESS].includes(deployment.status)).length > 0;
    };

    const hasCancelledDeployments = (record) => {
        const groupDeployments = deploymentStore.getDeploymentsByGroupId(record.id);
        return groupDeployments.filter((deployment) => [DeploymentStatus.CANCELLED].includes(deployment.status)).length > 0;
    };

    const columns: IBasicTableColumn[] = [
        {
            code: 'label',
            title: 'Deployment group',
            dataIndex: 'label',
            sorter: (a: IDisplayDeploymentGroup, b: IDisplayDeploymentGroup) => localeCompare(a.label, b.label),
            textSearchPlaceholder: 'Search Deployment group',
            onFilter: (searchString: string, group: IDisplayDeploymentGroup) =>
                hasContainsIgnoreCase(group.label, searchString),
            iconName: 'search',
            render: (text: string, record: IDisplayDeploymentGroup) => (
                <IconTextButton
                    onClick={(): void => {
                        props.setNavigateState({
                            groupId: record.id,
                            actionType: hasModifyDeploymentGroupPermission(record)
                                ? ACTION_TYPES.EDIT
                                : ACTION_TYPES.VIEW,
                        });
                        props.setPageType(PAGE_TYPES.MODIFY);
                    }}
                    label={text}
                    type='link'
                />
            ),
        },
        {
            code: 'accountName',
            title: 'Account',
            dataIndex: 'accountName',
            sorter: (a: IDisplayDeploymentGroup, b: IDisplayDeploymentGroup) =>
                localeCompare(b.accountName, a.accountName),
            textSearchPlaceholder: 'Search Account',
            onFilter: (searchString: string, group: IDisplayDeploymentGroup) =>
                hasContainsIgnoreCase(group.accountName, searchString),
        },
        TableUtils.createBasicEnumFilterColumn<IDisplayDeploymentGroup>({
            title: 'Type',
            key: 'type',
            filters: Object.values(DeploymentGroupType).map((type) => ({
                text: type,
                value: type,
            })),
        }),
        {
            code: 'deviceGroups',
            title: 'Device groups',
            dataIndex: 'deviceGroupLabelAsString',
            sorter: (a: IDisplayDeploymentGroup, b: IDisplayDeploymentGroup) =>
                localeCompare(b.deviceGroupLabelAsString, a.deviceGroupLabelAsString),
            textSearchPlaceholder: 'Search Device group',
            onFilter: (searchString: string, group: IDisplayDeploymentGroup) =>
                hasContainsIgnoreCase(group.deviceGroupLabelAsString, searchString),
        },
        {
            code: 'action',
            title: 'Action',
            dataIndex: 'action',
            render: (_, record: IDisplayDeploymentGroup) => (
                <IconTextButton
                    onClick={async (): Promise<void> => {
                        confirmDialog(
                            <div>
                                Any CANCELLED deployments associated with the <b>{record.label}</b> will be deleted also so
                                you will lose the history of deployments.
                                <br />
                                Do you want to delete this deployment group?
                            </div>,
                            () => onDeleteDeploymentGroup(record),
                        );
                    }}
                    label='Delete'
                    type='link'
                    disabled={!hasModifyDeploymentGroupPermission(record)}
                />
            ),
        },
    ];

    const onDeleteDeploymentGroup = async (group: IDisplayDeploymentGroup) => {
        const account = accountStore.getAccountByUuidOrParentUuidForClientAccount(group.accountUuid);
        if (!account) {
            return;
        }
        try {
            await deploymentStore.loadDeploymentsByGroupId(account.id, group.id, true);
            if (hasScheduledOrInProgressDeployments(group)) {
                const message =
                    'Deployment group has deployments IN PROGRESS or SCHEDULED. Please cancel the deployments first, and try to delete a group again.';
                showInformationDialog({
                    modalType: 'warning',
                    title: 'Deployment group',
                    content: message,
                });
                return;
            }
            return deleteDeploymentGroup(group);
        } catch (err) {
            notification.error({
                message: 'Delete deployment group error',
                description: getErrorMessage(err),
            });
        }
    };

    const toolbar: IAdvanceTableToolbarProps = {
        onReload: () => reloadData(true),
        extraItems: hasModifyPermission
            ? [
                <IconTextButton
                    iconName='plus'
                    key='addDeploymentGroup'
                    type='primary'
                    onClick={(): void => {
                        props.setNavigateState({
                            actionType: ACTION_TYPES.ADD,
                        });
                        props.setPageType(PAGE_TYPES.MODIFY);
                    }}
                    label={'Add New'}
                />,
            ]
            : [],
    };

    const selectAccountComponent = currentUser.accountType != AccountType.CLIENT && (
        <OrganisationSelect
            placeholder='Select Account'
            treeDataSource={selectAccountSource}
            expandAll={true}
            onChange={(value): void => {
                setSelectedAccount(accountStore.getAccountById(value));
            }}
            value={selectedAccount?.id}
            disabled={deploymentGroupStore.dataLoading || deviceGroupStore.dataLoading}
        />
    );

    return (
        <React.Fragment>
            {deploymentGroupStore.error ? (
                <DangerAlert message={deploymentGroupStore.error} className={deploymentGroupCss.alert} />
            ) : null}
            <AdvanceTable
                title={selectAccountComponent}
                loadingState={deploymentGroupStore.dataLoading || deviceGroupStore.dataLoading || deploymentGroupStore.dataLoading}
                table={{
                    columns: columns,
                    dataSource: displayDeploymentGroups,
                }}
                toolbar={toolbar}
            />
        </React.Fragment>
    );
});
